/**
 * Utah's official-results sources (the state's own words live in the state pack: scripts/state-coupling.test.ts). Salt Lake County
 * Health Department, read from CDP's public EHS portal (services/api/src/official/cdp.ts; docs/OFFICIAL-RESULTS.md §8), its scale,
 * its establishment-type words, and its violation-code mapping to the FDA Food Code (Utah adopts the 2022 Food Code in R392-100-4).
 */
import type { OfficialSource } from "../../official-sources.ts";
import type { OfficialCategory, OfficialScale } from "../../official.ts";

const SLCO_PORTAL = "https://public.cdpehs.com/UTEnvPbl/VW_EST_PUBLIC/ShowVW_EST_PUBLICTablePage.aspx";

export const SLCO_SOURCES: readonly OfficialSource[] = [
  {
    id: "slco", state: "UT", jurisdiction: "UT/salt-lake", department: "Salt Lake County Health Department", area: "Salt Lake County", status: "sync",
    format: "cdp_ehs", urls: [SLCO_PORTAL],
    cdp: {
      entryUrl: SLCO_PORTAL, areas: ["Food Service", "Temporary Event"], minGapMs: 3200, window: { tz: "America/Denver", startHour: 21, endHour: 6 },
      labels: {
        list: ["Click to See", "Establishment Name", "Address", "City", "Inspection Area"],
        history: ["Establishment Information", "Name", "Address", "City/State/ZIP", "Phone", "Establishment Type", "Inspection History", "Date", "Inspection Type", "Score", "Critical Violations", "Non-Critical Violations"],
        report: ["Inspection Date", "Inspection Type", "Score", "Number of Critical Violations", "Number of Non-Critical Violations", "Violation Summary", "Code", "Observed Violations", "Points", "Critical", "Occurrences", "COS", "PHR"],
        closures: ["Establishment Name", "Address", "Date Closed", "Date Reopened"],
      },
    },
    parser: "cdp", categories: true, expectedPlaces: 6500, updates: "every weekday evening (we read it on weekend nights, a quarter of places each weekend, all of them monthly)",
    basis: { kind: "facts", terms: "public record; the county's own inspection portal, read slowly at night", quote: "The only official source for Salt Lake County Health Department inspection information is the ”Inspection Reports” button on this website.", url: "https://www.saltlakecounty.gov/health/food-protection/inspections/" },
    flag: { clause: "The only official source … is the ”Inspection Reports” button", url: "https://www.saltlakecounty.gov/health/food-protection/inspections/" },
    attribution: "Salt Lake County Health Department, Inspection Reports",
    lookupUrl: "https://www.saltlakecounty.gov/health/inspection/",
    recordHint: "Open the county's Inspection Reports, choose Food Service, and search for the name.",
    // Short lines on purpose: this sentence is read on every Salt Lake County card, place page and hub (reading level under grade 3; hubs.test.ts measures it).
    howOften: { text: "The county inspects each food place one to three times a year. It does not say when it will come. How often depends on the place's risk level. Level 1: once a year. Level 2: every 9 months. Level 3: twice a year. Level 4: three times a year. A complaint can bring another visit.", url: "https://www.saltlakecounty.gov/health/food-protection/inspections/" },
    levelMonths: { 1: 12, 2: 9, 3: 6, 4: 4 },
  },
];

export const SLCO_SCALE: OfficialScale = {
  id: "UT/salt-lake:points", kind: "points_lower_better", unit: "points", noun: "point violations", lowerIsBetter: true, min: 0, max: 60,
  // Block starts in points (the range bar): the county adds 1, 3 or 6 per violation, so 0 is none cited, 1-5 is a couple of lower-risk items (1 and 3), 6-12 is one 6-point item up to a few, 13-24 is several, 25+ is many. A judgment on the county's own weights, not its data (no letter bands to use).
  edges: [0, 1, 6, 13, 25],
  meaning: "Each violation the inspector finds adds 1, 3 or 6 points, by how likely it is to cause illness. The points add up to the inspection score. The county uses no letter grade.",
  source: { citation: "Salt Lake County Health Department, How We Score and Rank Food Service Businesses: \"Violations observed during an inspection each carry a score of 1, 3, or 6 points depending on the severity of the violation\"", url: "https://www.saltlakecounty.gov/health/food-protection/inspections/" },
};

/**
 * Salt Lake's "Establishment Type" (the county's own words: "Restaurants: plated", "Restaurants: non-plated", "Commissary") and
 * inspection area → ours. Only what the words say: a name with "coffee" or "espresso" in it is a coffee place; "non-plated" is
 * counter service; a mobile or cart type is a truck; a Temporary Event area is a temp event.
 */
export function slcoCategory(type: string, area: string, name: string): OfficialCategory {
  const t = type.toLowerCase();
  if (/temporary event/i.test(area)) return "temp_event";
  if (/\b(coffee|espresso)\b/i.test(name)) return "coffee";
  if (/mobile|truck|cart|trailer/.test(t)) return "truck";
  if (/grocery|market|convenience|retail|store|deli|bakery/.test(t)) return "market";
  if (/non-plated/.test(t)) return "fast_food";
  if (/\bplated\b|restaurant|banquet|catering|tavern|bar\b/.test(t)) return "full_service";
  return "other";
}

/**
 * Salt Lake County Health Department violation codes ("4.3.59*") → the FDA Food Code 2022 section it cites → Form 3-A items (the
 * same item numbering as FORM_ITEMS / FDA_ITEM_FACTOR above). DERIVED, 2026-10-04: the county's report gives each code a
 * "public health rationale" label that is the title of the Food Code section it follows (4.4.101 "Equipment and Utensils-Air-Drying
 * Required" = 4-901.11; 4.6.50 "Controlling Pests" = 6-501.111). Utah adopts the 2022 Food Code in R392-100-4. The county
 * regulation with these numbers is Salt Lake County Health Department Health Regulation #5 "Food Sanitation" (adopted 1981, last
 * amended May 3, 2018; SLCO_REGULATION_URL, opened 2026-10-07: its §4.4.82 and §7.4.4 are the codes below). A trailing
 * "*" or "**" on a code marks the critical form of the same rule and is dropped. A code whose title fits two items (4.4.82
 * "Equipment, Food-Contact Surfaces, Nonfood-Contact Surfaces, and Utensils-Cleaning", Food Code 4-601.11 A, B and C) is left out:
 * it could be a food-contact surface (item 16) or a hood (item 49). Anything not listed is "not mapped", never clear.
 * Added 2026-10-18 (MEASURED on a live Salt Lake page whose snippet read "including when to Wash-Hands and Arms", the county's raw title):
 * the codes that page cited whose title is one Food Code section (fcquotes.ts holds each section's words) and whose Form 3-A item has a
 * found line that says what was cited (found.ts) and a check we keep (AHEAD): 4.2.12 "When to Wash-Hands and Arms" (2-301.14, item 8),
 * 4.3.31 in-use utensils (3-304.12, 43), 4.4.59 sanitizer test strips (4-302.14, 48), 4.4.63 good repair (4-501.11, 47), 4.4.76 chemical
 * sanitization (4-501.114, 16), 4.4.80 single-use articles (4-502.13, 45), 4.5.61 toilet room receptacle (5-501.17, 53), 4.7.2 working
 * containers' common name (7-102.11, 28). Still not mapped, on purpose: 4.3.45 "Miscellaneous Sources of Contamination" (3-307.11 is Form
 * 3-A item 15, whose found line says "Raw food not kept apart…", which that section does not say), 4.6.27 "Handwashing Signage" (6-301.14 is
 * item 10, whose found line says "Hand sink not ready to use": more than a missing sign), 4.3.24 food containers' common name (3-302.12 is
 * item 37, which AHEAD has no check for), 4.1.3 "Food Handler Training" (Utah law, no Form 3-A item).
 */
export const SLCO_CODE_FDA: Readonly<Record<string, { fda: string; items: readonly number[] }>> = {
  "4.2.12": { fda: "2-301.14", items: [8] },
  "4.2.18": { fda: "2-401.11", items: [6] },
  "4.3.31": { fda: "3-304.12", items: [43] },
  "4.3.37": { fda: "3-305.11", items: [39] },
  "4.3.57": { fda: "3-501.14", items: [20] },
  "4.3.59": { fda: "3-501.16", items: [22] },
  "4.3.60": { fda: "3-501.17", items: [23] },
  "4.4.1": { fda: "4-101.11", items: [47] },
  "4.4.11": { fda: "4-101.19", items: [47] },
  "4.4.13": { fda: "Part 4-2, design and construction", items: [47] },
  "4.4.61": { fda: "4-402.11", items: [47] },
  "4.4.59": { fda: "4-302.14", items: [48] },
  "4.4.63": { fda: "4-501.11", items: [47] },
  "4.4.64": { fda: "Part 4-2, cutting surfaces", items: [47] },
  "4.4.76": { fda: "4-501.114", items: [16] },
  "4.4.80": { fda: "4-502.13", items: [45] },
  "4.4.85": { fda: "4-602.13", items: [49] },
  "4.4.101": { fda: "4-901.11", items: [44] },
  "4.5.23": { fda: "5-204.11", items: [10] },
  "4.5.26": { fda: "5-205.11", items: [10] },
  "4.5.30": { fda: "5-205.15", items: [51] },
  "4.5.61": { fda: "5-501.17", items: [53] },
  "4.6.1": { fda: "6-101.11", items: [55] },
  "4.6.5": { fda: "6-201.13", items: [55] },
  "4.6.15": { fda: "6-202.15", items: [38] },
  "4.6.32": { fda: "6-304.11", items: [56] },
  "4.6.40": { fda: "6-501.11", items: [55] },
  "4.6.41": { fda: "6-501.12", items: [55] },
  "4.6.43": { fda: "6-501.14", items: [56] },
  "4.6.50": { fda: "6-501.111", items: [38] },
  "4.6.51": { fda: "6-501.112", items: [38] },
  "4.6.53": { fda: "6-501.114", items: [55] },
  "4.7.2": { fda: "7-102.11", items: [28] },
  "4.7.4": { fda: "7-201.11", items: [28] },
};
/** The county's food regulation (Health Regulation #5 "Food Sanitation", last amended May 3, 2018), opened 2026-10-07. */
export const SLCO_REGULATION_URL = "https://www.saltlakecounty.gov/globalassets/1-site-files/health/regulations/regulation-pdfs/food.pdf";
/**
 * Codes the county cites on its reports (MEASURED: the closure report in test/fixtures/cdp/report-closed.html) that its own
 * regulation defines but that map to NO single Form 3-A item, so they are never counted as "clear" (classifyCode: not mapped) and
 * never given an item we would have to invent:
 *  - 4.4.82 covers three subsections under different items: (i) food-contact surfaces clean to sight and touch (item 16) and (ii),
 *    (iii) cooking surfaces and nonfood-contact surfaces (item 49); the county's critical mark does not say which (MEASURED: its
 *    "4.4.82*" on the closure report is a ventilation hood, a nonfood-contact surface).
 *  - 7.4.4 (ii) is the county's permit action (summary suspension for an imminent health hazard), not a Food Code requirement.
 * They exist in the county's code book: a parsing rule or a hand entry may cite them (core checkRule, rereads.ts checkHandEntry).
 */
export const SLCO_CODES_UNMAPPED: Readonly<Record<string, { title: string; plain: string; section: string; quote: string; url: string; lastChecked: string; why: string }>> = {
  "4.4.82": {
    title: "Equipment, Food-Contact Surfaces, Nonfood-Contact Surfaces, and Utensils – Cleaning", plain: "Equipment or utensils not clean",
    section: "Health Regulation #5 §4.4.82", quote: "Equipment food-contact surfaces and utensils shall be clean to sight and touch.",
    url: SLCO_REGULATION_URL, lastChecked: "2026-10-07", why: "subsections fall under Form 3-A items 16 and 49; the citation does not say which",
  },
  "7.4.4 (ii)": {
    title: "Summary Permit Suspension", plain: "Permit suspended at once for an imminent health hazard",
    section: "Health Regulation #5 §7.4.4(ii)", quote: "The Department may summarily suspend, without prior warning, notice of a hearing or a hearing,",
    url: SLCO_REGULATION_URL, lastChecked: "2026-10-07", why: "a permit action, not a Food Code requirement: no Form 3-A item",
  },
};
/** "4.6.50**" → "4.6.50"; "7.4.4 (ii)" stays as is (a permit action, not a Food Code section: not mapped). */
export const slcoBase = (code: string) => code.trim().replace(/\*+$/, "").trim();


/**
 * A readable copy of a rule page that shows its text only in a browser (services/api/src/sourcecheck/job.ts; owner, 2026-10-11):
 * our pages keep citing the state's own adminrules address; the monthly re-check compares the quotes with this copy, and stamps only
 * while the copy is fresh (its Last-Modified within freshDays). MEASURED 2026-10-11: the LII page carries R392-100-4's words.
 */
export const UT_READABLE_COPIES: Readonly<Record<string, { url: string; freshDays: number }>> = {
  "https://adminrules.utah.gov/public/rule/R392-100/Current%20Rules": { url: "https://www.law.cornell.edu/regulations/utah/Utah-Admin-Code-R392-100-4", freshDays: 120 },
};

/**
 * "Salt Lake County cites it as 4.6.15": the county's own code number for a Food Code section, where its code book maps it
 * (SLCO_CODE_FDA), for a plan's law line (core citedplan.ts planLawAt). Null for another source or an unmapped section.
 */
export function countyCodeLine(sourceId: string, fdaSection: string): string | null {
  if (sourceId !== "slco") return null;
  const code = Object.entries(SLCO_CODE_FDA).find(([, v]) => v.fda === fdaSection)?.[0];
  return code ? `Salt Lake County cites it as ${code}` : null;
}

/**
 * A Utah rule that goes with a Food Code section, said only at Utah places (core citedplan.ts planLawAt). The shared fix plans carry the
 * Food Code's words only (state-coupling: a state's own rule is said through states/). 2-102.12 certified manager: Utah Code 26B-7-410(2)
 * ("Within 60 days of the termination of a certified food safety manager's employment ... designate another employee ... who shall
 * commence a department-approved food safety manager training course").
 */
export function stateLawLine(state: string | null | undefined, fdaSection: string): string | null {
  if (state !== "UT") return null;
  return fdaSection === "2-102.12" ? "Utah Code 26B-7-410: if your certified manager leaves, name another employee within 60 days who starts a department-approved manager course" : null;
}
